import React from 'react';
import { Button, Drawer, message, Skeleton, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import TablePaginationBar from '../../../../../components/TablePaginationBar';
import AsyncReportDownloadModal, {
    dashboardAsyncReportLabelI18nKeys,
} from '../../../../../components/asyncReportDownload';
import CentralParcelDetailDrawer from '../../../../central/parcels/components/CentralParcelDetailDrawer';
import { getUserInfo } from '../../../../../utils/auth';
import { formatUtcToLocal } from '../../../../../utils/timezoneFormatter';
import type { InhubTransportPerspective } from '../graphql/types';
import { fromWireIso } from '../graphql/timeRange';
import {
    GET_INHUB_TRANSPORT_BAG_DETAIL_PAGE,
    GET_INHUB_TRANSPORT_INBOUND_BAG_DETAIL_PAGE,
    type GetInhubTransportBagDetailPageResponse,
    type InhubTransportBagArrivalStatus,
    type InhubTransportBagDetailRowResp,
    type InhubTransportDetailInput,
    type InhubTransportDetailStatusFilter
} from '../graphql/queries/getInhubTransportBagDetailPage';
import {
    GET_INHUB_TRANSPORT_INBOUND_PARCEL_DETAIL_PAGE,
    GET_INHUB_TRANSPORT_PARCEL_DETAIL_PAGE,
    type GetInhubTransportParcelDetailPageResponse,
    type InhubTransportParcelDetailRowResp
} from '../graphql/queries/getInhubTransportParcelDetailPage';
import {
    GENERATE_INHUB_TRANSPORT_BAG_DETAIL_EXPORT,
    GENERATE_INHUB_TRANSPORT_INBOUND_BAG_DETAIL_EXPORT,
    type GenerateInhubTransportBagDetailExportResponse,
    type GenerateInhubTransportBagDetailExportVariables
} from '../graphql/mutations/generateInhubTransportBagDetailExport';
import {
    GENERATE_INHUB_TRANSPORT_INBOUND_PARCEL_DETAIL_EXPORT,
    GENERATE_INHUB_TRANSPORT_PARCEL_DETAIL_EXPORT,
    type GenerateInhubTransportParcelDetailExportResponse,
    type GenerateInhubTransportParcelDetailExportVariables
} from '../graphql/mutations/generateInhubTransportParcelDetailExport';

const I18N_BASE = 'wms.delivery.inhubDashboard.transport';
const DEFAULT_PAGE_SIZE = 10;

export type TransportDetailGranularity = 'BAG' | 'PARCEL';

interface Props {
    open: boolean;
    onClose: () => void;
    granularity: TransportDetailGranularity;
    hubId: number;
    rangeStart: string;
    rangeEnd: string;
    fromHubId: number | null;
    fromHubName: string | null;
    toHubId: number | null;
    toHubName: string | null;
    perspective: InhubTransportPerspective;
    statusFilter: InhubTransportDetailStatusFilter;
}

const TransportDetailDrawer: React.FC<Props> = ({
    open,
    onClose,
    granularity,
    hubId,
    rangeStart,
    rangeEnd,
    fromHubId,
    fromHubName,
    toHubId,
    toHubName,
    perspective,
    statusFilter
}) => {
    const { t } = useTranslation();
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
    const [parcelDetailVisible, setParcelDetailVisible] = React.useState(false);
    const [selectedOrderId, setSelectedOrderId] = React.useState<string>('');

    React.useEffect(() => {
        if (open) {
            setPage(1);
        }
    }, [open, granularity, hubId, rangeStart, rangeEnd, fromHubId, toHubId, perspective, statusFilter]);

    const isOutbound = perspective === 'OUTBOUND';

    const filterVars: InhubTransportDetailInput = {
        hubId,
        rangeStart,
        rangeEnd,
        fromHubId,
        toHubId,
        statusFilter,
        page,
        pageSize
    };

    const bagQuery = useQuery<GetInhubTransportBagDetailPageResponse>(
        isOutbound ? GET_INHUB_TRANSPORT_BAG_DETAIL_PAGE : GET_INHUB_TRANSPORT_INBOUND_BAG_DETAIL_PAGE,
        {
            variables: { filter: filterVars },
            skip: !open || granularity !== 'BAG',
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true
        }
    );
    const parcelQuery = useQuery<GetInhubTransportParcelDetailPageResponse>(
        isOutbound ? GET_INHUB_TRANSPORT_PARCEL_DETAIL_PAGE : GET_INHUB_TRANSPORT_INBOUND_PARCEL_DETAIL_PAGE,
        {
            variables: { filter: filterVars },
            skip: !open || granularity !== 'PARCEL',
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true
        }
    );

    const loading = granularity === 'BAG' ? bagQuery.loading : parcelQuery.loading;
    const bagPage = bagQuery.data?.getInhubTransportBagDetailPage
        ?? bagQuery.data?.getInhubTransportInboundBagDetailPage;
    const parcelPage = parcelQuery.data?.getInhubTransportParcelDetailPage
        ?? parcelQuery.data?.getInhubTransportInboundParcelDetailPage;
    const bagRows = bagPage?.content ?? [];
    const parcelRows = parcelPage?.content ?? [];
    const total = granularity === 'BAG'
        ? bagPage?.totalElements ?? 0
        : parcelPage?.totalElements ?? 0;

    const [exportBag, { loading: exportingBag }] = useMutation<
        GenerateInhubTransportBagDetailExportResponse,
        GenerateInhubTransportBagDetailExportVariables
    >(isOutbound
        ? GENERATE_INHUB_TRANSPORT_BAG_DETAIL_EXPORT
        : GENERATE_INHUB_TRANSPORT_INBOUND_BAG_DETAIL_EXPORT);
    const [exportParcel, { loading: exportingParcel }] = useMutation<
        GenerateInhubTransportParcelDetailExportResponse,
        GenerateInhubTransportParcelDetailExportVariables
    >(isOutbound
        ? GENERATE_INHUB_TRANSPORT_PARCEL_DETAIL_EXPORT
        : GENERATE_INHUB_TRANSPORT_INBOUND_PARCEL_DETAIL_EXPORT);
    const exporting = granularity === 'BAG' ? exportingBag : exportingParcel;
    const [downloadModalVisible, setDownloadModalVisible] = React.useState(false);
    const [generatedFileName, setGeneratedFileName] = React.useState('');

    const handleExport = async () => {
        try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const filter: InhubTransportDetailInput = {
                hubId,
                rangeStart,
                rangeEnd,
                fromHubId,
                toHubId,
                statusFilter,
                page: 1,
                pageSize: 1
            };
            let fileName: string | undefined;
            if (granularity === 'BAG') {
                const { data } = await exportBag({ variables: { filter, timezone: tz } });
                fileName = data?.generateInhubTransportBagDetailExport
                    ?? data?.generateInhubTransportInboundBagDetailExport;
            } else {
                const { data } = await exportParcel({ variables: { filter, timezone: tz } });
                fileName = data?.generateInhubTransportParcelDetailExport
                    ?? data?.generateInhubTransportInboundParcelDetailExport;
            }
            if (fileName) {
                setGeneratedFileName(fileName);
                setDownloadModalVisible(true);
            }
        } catch (e: unknown) {
            const errMsg = e instanceof Error ? e.message : String(e);
            message.error(errMsg);
        }
    };

    const renderTime = (v: string | null) => formatUtcToLocal(v);
    const renderText = (v: string | null) => (v && v.trim() !== '' ? v : '-');

    const arrivalStatusTagColor: Record<InhubTransportBagArrivalStatus, string> = {
        ARRIVED: 'green',
        NOT_ARRIVED_WITHIN_72H: 'gold',
        NOT_ARRIVED_OVER_72H: 'volcano'
    };

    const bagColumns: ColumnsType<InhubTransportBagDetailRowResp> = [
        {
            title: t(`${I18N_BASE}.detail.columns.bagNumber`),
            dataIndex: 'bagNumber',
            width: 180
        },
        {
            title: t(`${I18N_BASE}.detail.columns.taskNumber`),
            dataIndex: 'taskNumber',
            width: 160,
            render: renderText
        },
        {
            title: t(`${I18N_BASE}.detail.columns.taskDate`),
            dataIndex: 'taskDate',
            width: 120,
            render: renderText
        },
        {
            title: t(`${I18N_BASE}.detail.columns.plannedDepartureTime`),
            dataIndex: 'plannedDepartureTime',
            width: 180,
            render: renderTime
        },
        {
            title: t(`${I18N_BASE}.detail.columns.actualDepartureTime`),
            dataIndex: 'actualDepartureTime',
            width: 180,
            render: renderTime
        },
        {
            title: t(`${I18N_BASE}.detail.columns.arrivalStatus`),
            dataIndex: 'arrivalStatus',
            width: 160,
            render: (s: InhubTransportBagArrivalStatus) => (
                <Tag color={arrivalStatusTagColor[s]} bordered={false}>
                    {t(`${I18N_BASE}.detail.arrivalStatus.${s}`)}
                </Tag>
            )
        }
    ];

    const parcelColumns: ColumnsType<InhubTransportParcelDetailRowResp> = [
        {
            title: t(`${I18N_BASE}.detail.columns.trackingNumber`),
            dataIndex: 'trackingNumber',
            width: 180,
            render: (text: string, record: InhubTransportParcelDetailRowResp) => (
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        setSelectedOrderId(String(record.orderId));
                        setParcelDetailVisible(true);
                    }}
                    style={{ color: '#1890ff' }}
                >
                    {text ?? '-'}
                </a>
            )
        },
        {
            title: t(`${I18N_BASE}.detail.columns.overallStatus`),
            dataIndex: 'overallStatus',
            width: 140,
            render: (s: string) => (s ? t(`enum.overallStatus.${s}`, { defaultValue: s }) : '-')
        },
        {
            title: t(`${I18N_BASE}.detail.columns.bagNumber`),
            dataIndex: 'bagNumber',
            width: 180
        },
        {
            title: t(`${I18N_BASE}.detail.columns.taskNumber`),
            dataIndex: 'taskNumber',
            width: 160,
            render: renderText
        },
        {
            title: t(`${I18N_BASE}.detail.columns.taskDate`),
            dataIndex: 'taskDate',
            width: 120,
            render: renderText
        },
        {
            title: t(`${I18N_BASE}.detail.columns.plannedDepartureTime`),
            dataIndex: 'plannedDepartureTime',
            width: 180,
            render: renderTime
        },
        {
            title: t(`${I18N_BASE}.detail.columns.actualDepartureTime`),
            dataIndex: 'actualDepartureTime',
            width: 180,
            render: renderTime
        }
    ];

    const statusTagColor: Record<InhubTransportDetailStatusFilter, string> = {
        DEPARTED: 'blue',
        ARRIVED: 'green',
        NOT_ARRIVED: 'red',
        OVER_72H: 'volcano'
    };
    const statusLabel = t(`${I18N_BASE}.detail.statusFilter.${statusFilter}`);
    const granularityLabel = t(`${I18N_BASE}.detail.granularity.${granularity}`);
    const rangeLabel = `${fromWireIso(rangeStart).format('MM-DD HH:mm')} ~ ${fromWireIso(rangeEnd).format('MM-DD HH:mm')}`;

    const fromHubLabel = fromHubName ?? t(`${I18N_BASE}.detail.allHubs`);
    const toHubLabel = toHubName ?? t(`${I18N_BASE}.detail.allHubs`);
    const title = (
        <div className="flex items-center gap-2">
            <span>{t(`${I18N_BASE}.detail.title`)}</span>
            <Tag color="default" bordered={false}>{granularityLabel}</Tag>
            <Tag color={statusTagColor[statusFilter]} bordered={false}>{statusLabel}</Tag>
            <span className="text-xs text-gray-500">{fromHubLabel} → {toHubLabel} · {rangeLabel}</span>
        </div>
    );

    return (
        <Drawer
            title={title}
            placement="right"
            width={granularity === 'PARCEL' ? 1080 : 1040}
            open={open}
            onClose={onClose}
            destroyOnClose
            closable={false}
            extra={<CloseOutlined onClick={onClose} className="cursor-pointer" />}
        >
            <div className="flex justify-end mb-3">
                <Button
                    type="primary"
                    loading={exporting}
                    onClick={handleExport}
                    disabled={total === 0}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t(`${I18N_BASE}.detail.export.button`)}</span>
                </Button>
            </div>
            {loading && bagRows.length === 0 && parcelRows.length === 0 ? (
                <Skeleton active paragraph={{ rows: 6 }} />
            ) : granularity === 'BAG' ? (
                <Table<InhubTransportBagDetailRowResp>
                    rowKey={(r, idx) => `${r.bagNumber}-${idx}`}
                    columns={bagColumns}
                    dataSource={bagRows}
                    pagination={false}
                    size="middle"
                    scroll={{ x: 980 }}
                    loading={loading}
                />
            ) : (
                <Table<InhubTransportParcelDetailRowResp>
                    rowKey={(r, idx) => `${r.trackingNumber}-${idx}`}
                    columns={parcelColumns}
                    dataSource={parcelRows}
                    pagination={false}
                    size="middle"
                    scroll={{ x: 1140 }}
                    loading={loading}
                />
            )}
            <TablePaginationBar
                className="mt-4"
                total={total}
                current={page}
                pageSize={pageSize}
                onChange={(p, ps) => {
                    setPage(p);
                    setPageSize(ps);
                }}
                onShowSizeChange={(_, ps) => {
                    setPage(1);
                    setPageSize(ps);
                }}
            />

            <CentralParcelDetailDrawer
                visible={parcelDetailVisible}
                orderId={selectedOrderId}
                userRole={getUserInfo()?.account?.role?.name ?? ''}
                onClose={() => setParcelDetailVisible(false)}
            />
            <AsyncReportDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={generatedFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.inhubTransport}
            />
        </Drawer>
    );
};

export default TransportDetailDrawer;
